import React, { useEffect, useState } from 'react';
import { collection, addDoc, doc, getDocs, query, orderBy, limit, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { Player, SyncRequest } from '@/types';
import { UserPlus, ShieldCheck, ShieldAlert, Link2, CheckCircle2, Circle, ExternalLink, RefreshCw } from 'lucide-react';

// A players-portal signup (users doc, role 'player') waiting to be connected to a
// player card. usbcId is what the kid typed at signup — coach verifies it.
export interface SignupUser {
  uid: string;
  email: string;
  displayName: string;
  usbcId?: string;
  createdAt: Date;
}

interface OnboardingTabProps {
  signups: SignupUser[];
  players: Player[];
  onEditPlayer: (player: Player) => void;
  onRefresh: () => Promise<void>;
}

// The funnel a new bowler moves through. Everything after "linked" already has
// UI on the roster tab — this tab is the intake end of the pipe.
export const findLinkedPlayer = (u: SignupUser, players: Player[]): Player | undefined =>
  players.find(
    (p) =>
      (p.email && u.email && p.email.toLowerCase() === u.email.toLowerCase()) ||
      p.id === u.uid ||
      p.uid === u.uid
  );

export const OnboardingTab: React.FC<OnboardingTabProps> = ({ signups, players, onEditPlayer, onRefresh }) => {
  const { currentUser } = useAuth();
  const [linkTarget, setLinkTarget] = useState<Record<string, string>>({}); // signup uid -> player id
  const [busy, setBusy] = useState<string | null>(null); // signup uid being processed
  const [error, setError] = useState('');
  // Latest sync request per player (the "Sync now" queue, processed by the Mac
  // runner every ~15 min)
  const [syncByPlayer, setSyncByPlayer] = useState<Record<string, SyncRequest>>({});

  const loadSyncRequests = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'syncRequests'), orderBy('requestedAt', 'desc'), limit(50)));
      const map: Record<string, SyncRequest> = {};
      for (const d of snap.docs) {
        const r = { id: d.id, ...d.data(), requestedAt: d.data().requestedAt?.toDate() || new Date() } as SyncRequest;
        if (r.playerId && !map[r.playerId]) map[r.playerId] = r; // newest first wins
      }
      setSyncByPlayer(map);
    } catch (e) {
      console.error('syncRequests load failed', e);
    }
  };

  useEffect(() => {
    loadSyncRequests();
  }, []);

  const queueSync = async (playerId: string, playerName: string, usbcId?: string) => {
    try {
      await addDoc(collection(db, 'syncRequests'), {
        playerId,
        playerName,
        ...(usbcId ? { usbcId } : {}),
        requestedBy: currentUser?.uid || '',
        requestedAt: serverTimestamp(),
        status: 'pending',
      });
      await loadSyncRequests();
    } catch (e) {
      console.error('queue sync failed', e);
      setError('Could not queue the sync, check console.');
    }
  };

  const pending = signups.filter((u) => !findLinkedPlayer(u, players));
  // Linked but not yet live: still needs USBC verify / consent / publish
  const inProgress = signups
    .map((u) => ({ user: u, player: findLinkedPlayer(u, players) }))
    .filter((x): x is { user: SignupUser; player: Player } => !!x.player && !(x.player.published && x.player.consentOnFile));

  const candidates = [...players]
    .filter((p) => p.removed !== true)
    .sort((a, b) => {
      const aTeam = a.willardTeam ? 0 : 1;
      const bTeam = b.willardTeam ? 0 : 1;
      if (aTeam !== bTeam) return aTeam - bTeam;
      return a.name.localeCompare(b.name);
    });

  const handleLink = async (u: SignupUser) => {
    const playerId = linkTarget[u.uid];
    if (!playerId) return;
    setBusy(u.uid);
    setError('');
    try {
      const target = players.find((p) => p.id === playerId);
      await updateDoc(doc(db, 'players', playerId), {
        email: u.email, // the claim key, kid's dashboard resolves by this
        // Keep a coach-set USBC id; otherwise take what the kid entered at signup
        ...(!target?.usbcId && u.usbcId ? { usbcId: u.usbcId } : {}),
        updatedAt: serverTimestamp(),
      });
      // Onboarding = pull their data now, not tonight
      await queueSync(playerId, target?.name || u.displayName || u.email, target?.usbcId || u.usbcId);
      await onRefresh();
    } catch (e) {
      console.error('Link failed:', e);
      setError('Could not link, check console.');
    } finally {
      setBusy(null);
    }
  };

  const handleCreateCard = async (u: SignupUser) => {
    setBusy(u.uid);
    setError('');
    try {
      const created = await addDoc(collection(db, 'players'), {
        uid: u.uid,
        name: u.displayName || u.email,
        email: u.email,
        ...(u.usbcId ? { usbcId: u.usbcId } : {}),
        programId: 'willard-tigers',
        teamIds: [],
        grade: '',
        graduationYear: 0,
        averageScore: 0,
        highGame: 0,
        gamesPlayed: 0,
        isActive: true,
        // New signups are joining the Willard team (the Stats sync only manages
        // docs it created, so this flag is safe to set here)
        willardTeam: true,
        published: false,
        consentOnFile: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      // Onboarding = pull their data now, not tonight
      await queueSync(created.id, u.displayName || u.email, u.usbcId);
      await onRefresh();
    } catch (e) {
      console.error('Create failed:', e);
      setError('Could not create the player card, check console.');
    } finally {
      setBusy(null);
    }
  };

  const Chip: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        ok ? 'bg-green-100 text-green-800' : 'bg-tiger-neutral-100 text-tiger-neutral-500'
      }`}
    >
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
      {label}
    </span>
  );

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* New signups waiting on the coach */}
      <div className="bg-white rounded-2xl shadow-tiger-lg overflow-hidden">
        <div className="p-6 border-b border-tiger-neutral-200">
          <h2 className="text-xl font-display font-black text-tiger-primary-black">
            New Signups
          </h2>
          <p className="text-sm text-tiger-neutral-500 mt-1">
            Kids who created a portal account but aren't connected to a player card yet.
            Link them to their card (or create one), that queues a sync automatically, so their
            stats and leagues pull in within ~15 minutes. Verify the USBC # against their card,
            then pick their leagues (clipboard icon on the roster row) and flip consent + publish.
          </p>
        </div>

        {pending.length === 0 ? (
          <div className="text-center py-12">
            <UserPlus className="w-16 h-16 text-tiger-neutral-300 mx-auto mb-4" />
            <p className="text-tiger-neutral-600">No new signups waiting</p>
          </div>
        ) : (
          <div className="divide-y divide-tiger-neutral-200">
            {pending.map((u) => (
              <div key={u.uid} className="p-6 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-black text-lg text-tiger-primary-black">
                        {u.displayName || u.email}
                      </h3>
                      {u.usbcId ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-willard-taupe text-tiger-primary-black text-xs font-bold">
                          <ShieldCheck className="w-3.5 h-3.5" /> USBC {u.usbcId}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold">
                          <ShieldAlert className="w-3.5 h-3.5" /> No USBC # yet
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-tiger-neutral-600 mt-1">
                      {u.email} · signed up {u.createdAt.toLocaleDateString()}
                    </p>
                  </div>
                  {u.usbcId && (
                    <a
                      href="https://webapps.bowl.com/USBCFindA/Home/Member"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-tiger-neutral-600 hover:text-tiger-primary-black underline"
                    >
                      Verify on bowl.com <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={linkTarget[u.uid] || ''}
                    onChange={(e) => setLinkTarget((prev) => ({ ...prev, [u.uid]: e.target.value }))}
                    className="px-3 py-2 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black text-sm min-w-56"
                  >
                    <option value="">Link to existing bowler…</option>
                    {candidates.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.willardTeam ? '' : ' (tracked, not Willard team)'}
                        {p.email ? `, already linked to ${p.email}` : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleLink(u)}
                    disabled={!linkTarget[u.uid] || busy === u.uid}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-tiger-primary-black text-white text-sm font-bold rounded-lg hover:bg-tiger-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Link2 className="w-4 h-4" /> Link
                  </button>
                  <span className="text-sm text-tiger-neutral-400">or</span>
                  <button
                    onClick={() => handleCreateCard(u)}
                    disabled={busy === u.uid}
                    className="inline-flex items-center gap-2 px-4 py-2 border-2 border-tiger-neutral-300 text-tiger-neutral-700 text-sm font-bold rounded-lg hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" /> Create new player card
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Linked, still moving through the funnel */}
      <div className="bg-white rounded-2xl shadow-tiger-lg overflow-hidden">
        <div className="p-6 border-b border-tiger-neutral-200">
          <h2 className="text-xl font-display font-black text-tiger-primary-black">
            Onboarding In Progress
          </h2>
          <p className="text-sm text-tiger-neutral-500 mt-1">
            Linked accounts that aren't live on the site yet, still need a verified USBC #, a signed
            consent form, or the publish flip.
          </p>
        </div>

        {inProgress.length === 0 ? (
          <p className="text-tiger-neutral-600 text-center py-10">
            Nobody in the funnel, every linked account is published.
          </p>
        ) : (
          <div className="divide-y divide-tiger-neutral-200">
            {inProgress.map(({ user, player }) => {
              const sync = syncByPlayer[player.id];
              return (
                <div key={user.uid} className="p-6 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-tiger-primary-black">{player.name}</h3>
                    <p className="text-sm text-tiger-neutral-600">{user.email}</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Chip ok={!!player.usbcId} label={player.usbcId ? `USBC ${player.usbcId}` : 'USBC #'} />
                      <Chip ok={!!player.consentOnFile} label="Consent form" />
                      <Chip ok={!!player.published} label="Published" />
                      {sync && (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                            sync.status === 'done'
                              ? 'bg-green-100 text-green-800'
                              : sync.status === 'error'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-blue-100 text-blue-800'
                          }`}
                          title={sync.note || ''}
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${sync.status === 'pending' || sync.status === 'running' ? 'animate-spin' : ''}`} />
                          {sync.status === 'pending'
                            ? 'Sync queued'
                            : sync.status === 'running'
                              ? 'Syncing…'
                              : sync.status === 'done'
                                ? 'Synced'
                                : 'Sync failed'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => queueSync(player.id, player.name, player.usbcId)}
                      disabled={sync?.status === 'pending' || sync?.status === 'running'}
                      title="Queue a stats + league scrape now (the Mac runner picks it up within ~15 min)"
                      className="px-4 py-2 border-2 border-tiger-neutral-300 text-tiger-neutral-700 text-sm font-bold rounded-lg hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <RefreshCw className="w-4 h-4" /> Sync now
                    </button>
                    <button
                      onClick={() => onEditPlayer(player)}
                      className="px-4 py-2 bg-tiger-primary-black text-white text-sm font-bold rounded-lg hover:bg-tiger-neutral-800 transition-colors"
                    >
                      Open player card
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
